import { useQuery } from '@tanstack/react-query'
import { agentDefsApi, agentTemplatesApi } from '../api/agents'
import { api } from '../api/client'
import { messagesApi } from '../api/messages'
import { topicsApi } from '../api/topics'
import { meApi, workspacesApi } from '../api/workspaces'
import type {
  AgentDefInfo,
  DeploymentFeatures,
  PromptVersion,
  ThreadView,
  TopicInfo,
  VersionInfo,
} from '../types/api'
import {
  agentQueryKeys,
  providerQueryKeys,
  topicQueryKeys,
} from '../utils/queryKeys'

export { agentQueryKeys, providerQueryKeys, topicQueryKeys }

/**
 * Shared react-query hooks for data fetched in more than one place. Each hook
 * owns its key, queryFn, and cache config once, so call sites can't drift apart
 * on staleTime/enabled (they had — e.g. the topics guard; see useTopicsQuery).
 */

/** Current user + memberships. Read by account/billing/workspace-settings. */
export function useMeQuery() {
  return useQuery({ queryKey: ['me'], queryFn: meApi.get, staleTime: 60_000 })
}

/** Backend build version + configured features. Immutable per session. */
function useVersionQuery() {
  return useQuery({
    queryKey: ['version'],
    queryFn: () => api.get<VersionInfo>('/api/version'),
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * Which optional services this deployment has configured, for hiding chrome
 * that would lead nowhere. Only ever gates nav items and tab buttons — routes
 * stay registered and deep links keep working, so an unresolved query can't
 * strand anyone.
 *
 * Defaults to all-off while loading: a self-hosted install showing billing
 * controls it doesn't have is the case this exists to prevent, and the reverse
 * (a hosted install revealing its billing item a beat late) is cosmetic.
 */
export function useFeatures(): DeploymentFeatures {
  const { data } = useVersionQuery()
  return data?.features ?? { billing: false, email: false, spend_caps: false }
}

/** The user's workspaces. */
export function useWorkspacesQuery() {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
    staleTime: 30_000,
  })
}

/** Active and paused agent definitions for one workspace. Lifecycle management
 * may opt into soft-deleted rows without sharing their cache entry with normal
 * topic and management views. */
export function useWorkspaceAgentsQuery(
  workspaceId: string | null | undefined,
  opts?: {
    enabled?: boolean
    includeDeleted?: boolean
    staleTime?: number
  },
) {
  const id = workspaceId ?? ''
  const includeDeleted = opts?.includeDeleted ?? false
  return useQuery<AgentDefInfo[]>({
    queryKey: agentQueryKeys.workspace(id, includeDeleted),
    queryFn: () =>
      includeDeleted
        ? agentDefsApi.listForWorkspace(id, { includeDeleted: true })
        : agentDefsApi.listForWorkspace(id),
    enabled: !!workspaceId && (opts?.enabled ?? true),
    staleTime: opts?.staleTime ?? 30_000,
  })
}

/** Authoritative single-topic read. Unlike the workspace topic list, this is
 * neither capped nor coupled to the application's currently active workspace. */
export function useTopicQuery(
  topicId: string | null | undefined,
  opts?: { enabled?: boolean; staleTime?: number },
) {
  const id = topicId ?? ''
  return useQuery<TopicInfo>({
    queryKey: topicQueryKeys.detail(id),
    queryFn: () => topicsApi.get(id, { noCache: true }),
    enabled: !!topicId && (opts?.enabled ?? true),
    staleTime: opts?.staleTime ?? 10_000,
  })
}

/** Threads in a topic, scoped to a lifecycle bucket. Read by the composer,
 * thread list, thread view, and rail. The key includes `view` so each bucket
 * (active/archived/trash) caches independently; prefix-invalidations on
 * `['threads', topicId]` (e.g. in useWebSocket) refresh all views at once. */
export function useThreadsQuery(
  topicId: string,
  view: ThreadView = 'active',
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['threads', topicId, view],
    queryFn: () => messagesApi.listThreads(topicId, view),
    staleTime: 5_000,
    enabled: !!topicId && (opts?.enabled ?? true),
  })
}

/** Workspace-wide waiting-on-you list (unresolved agent review items),
 * oldest first. Powers the redesigned rail badge and inbox view. Refreshed
 * by review-lifecycle, resource_changed, and resource-mutating tool_finish
 * events (see ws/eventRouter). */
export function useWaitingQuery(wsId: string, enabled = true) {
  return useQuery({
    queryKey: ['waiting', wsId],
    queryFn: () => messagesApi.waiting(wsId),
    staleTime: 5_000,
    enabled: enabled && !!wsId,
  })
}

/** Saved agent templates for a workspace. `enabled` gates it (the create form
 * only). Keyed by workspace so switching workspaces refetches. */
export function useAgentTemplatesQuery(wsId: string, enabled = true) {
  return useQuery({
    queryKey: ['agent-templates', wsId],
    queryFn: () => agentTemplatesApi.list(wsId),
    staleTime: 60_000,
    enabled: enabled && !!wsId,
  })
}

/**
 * Prompt-version history for an agent. Only existing agents have history, so
 * `enabled` gates on having both ids. The editor unmounts on every successful
 * save, so a modest staleTime still yields a fresh list on reopen.
 */
export function useAgentPromptVersionsQuery(
  workspaceId: string,
  agentId: string,
  enabled = true,
) {
  return useQuery<PromptVersion[]>({
    queryKey: ['agent-prompt-versions', workspaceId, agentId],
    queryFn: () => agentDefsApi.listPromptVersions(workspaceId, agentId),
    enabled: enabled && !!workspaceId && !!agentId,
    staleTime: 10_000,
  })
}
